import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Button, IconButton, Skeleton, Typography } from '@mui/material'

import { ChatDateDivider } from './ChatDateDivider'
import { LessonChatWidgetCard } from './LessonChatWidgetCard'
import { MessageBubble } from './MessageBubble'
import { localDayKey } from './chatDay'
import { isLessonChatWidget } from '@/api/chat'
import type { ChatMessage, LessonChatWidgetMeta } from '@/api/chat'
import { useChatStore } from '@/stores/chatStore'

/** Потолок автодогрузки: 10 страниц по 30 сообщений хватает на любой урок. */
const AUTO_LOAD_PAGE_LIMIT = 10

/**
 * Запас в пикселях, в пределах которого лента считается прокрученной к концу.
 * Порядка одного пузыря: догрузившаяся картинка успевает подрасти на десяток
 * пикселей раньше, чем мы спросим, у конца ли читатель.
 */
const BOTTOM_THRESHOLD_PX = 64

function isAtBottom(container: HTMLElement): boolean {
  return container.scrollHeight - container.scrollTop - container.clientHeight <= BOTTOM_THRESHOLD_PX
}

interface ChatThreadProps {
  roomId: number | null
  /** Показывать имя автора над пузырём (групповая переписка и чат урока). */
  showAuthor: boolean
  canWrite: boolean
  currentUserId: number
  onEdit: (message: ChatMessage) => void
  onDelete: (message: ChatMessage) => void
  onCopyText: (message: ChatMessage) => void
  onToggleReaction: (messageId: number, emoji: string) => void
  /** Клик по плашке истории урока; без обработчика плашка не кликабельна. */
  onOpenLessonHistory?: (meta: LessonChatWidgetMeta) => void
  /**
   * Отмечать сообщения прочитанными при просмотре. Выключается там, где лента
   * показана мельком и «прочитано» было бы неправдой.
   */
  autoMarkRead?: boolean
  emptyText?: string
  /**
   * Узкая колонка (панель урока, модалка): лента и так внутри чужих отступов,
   * своими она съедала бы ширину пузырей второй раз.
   */
  dense?: boolean
  /** Архивная лента: реакции скрыты, действия ленты только на чтение. */
  readOnly?: boolean
  /**
   * Догружать историю самостоятельно, без кнопки. Для переписки одного урока:
   * она конечна, и «Загрузить ещё» посреди законченного разговора — лишний шаг.
   */
  autoLoadAll?: boolean
  /** Скрыть разделители дней: переписка урока умещается в один день. */
  hideDateDividers?: boolean
}

/**
 * Лента сообщений одной комнаты: дневные разделители, догрузка истории,
 * автоскролл к последнему сообщению и отметка прочтения.
 *
 * Общая для страницы чата, панели чата урока и модалки истории урока — иначе
 * три ленты разъезжались бы по поведению после первой же правки.
 */
export function ChatThread({
  roomId,
  showAuthor,
  canWrite,
  currentUserId,
  onEdit,
  onDelete,
  onCopyText,
  onToggleReaction,
  onOpenLessonHistory,
  autoMarkRead = true,
  emptyText,
  readOnly = false,
  dense = false,
  autoLoadAll = false,
  hideDateDividers = false,
}: ChatThreadProps) {
  const { t } = useTranslation()
  const byRoom = useChatStore((s) => s.byRoom)
  const fetchMessages = useChatStore((s) => s.fetchMessages)
  const markRead = useChatStore((s) => s.markRead)

  const scrollRef = useRef<HTMLDivElement | null>(null)
  /**
   * Последнее показанное сообщение. Автоскролл привязан к нему, а не к длине
   * ленты: «Загрузить ещё» подклеивает страницу в начало и длину тоже меняет —
   * пользователя тогда швыряло бы из прочитанного места в конец.
   */
  const lastSeenIdRef = useRef<number | null>(null)

  /** Сколько страниц догружено автоматически: страховка от бесконечного цикла. */
  const autoLoadedPagesRef = useRef(0)
  /** Потолок автодогрузки исчерпан — дальше историю тянет пользователь кнопкой. */
  const [autoLoadExhausted, setAutoLoadExhausted] = useState(false)

  /**
   * Замер прокрутки перед догрузкой истории. Страница встаёт в начало ленты и
   * сдвигает всё вниз на свою высоту: без компенсации читателя уносит от того
   * места, ради которого он и полез вверх.
   */
  const prependAnchorRef = useRef<{
    scrollHeight: number
    scrollTop: number
    atBottom: boolean
  } | null>(null)

  const roomState = roomId !== null ? byRoom[roomId] : undefined
  // Через useMemo: `?? []` создавал новый массив на каждый рендер, и
  // группировка ниже пересчитывалась даже когда лента не менялась.
  const messages = useMemo(() => roomState?.messages ?? [], [roomState?.messages])

  useEffect(() => {
    if (roomId !== null && !byRoom[roomId]?.loaded) {
      void fetchMessages(roomId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId])

  useEffect(() => {
    lastSeenIdRef.current = null
    autoLoadedPagesRef.current = 0
    prependAnchorRef.current = null
    setAutoLoadExhausted(false)
  }, [roomId])

  /** Догрузить страницу истории, запомнив, куда смотрел пользователь. */
  const loadOlder = useCallback(() => {
    if (roomId === null) return

    const container = scrollRef.current
    prependAnchorRef.current =
      container === null
        ? null
        : {
            scrollHeight: container.scrollHeight,
            scrollTop: container.scrollTop,
            atBottom: isAtBottom(container),
          }

    void fetchMessages(roomId, { loadMore: true })
  }, [roomId, fetchMessages])

  // Компенсация до отрисовки кадра (useLayoutEffect): в обычном эффекте
  // пользователь успел бы увидеть рывок ленты.
  useLayoutEffect(() => {
    const anchor = prependAnchorRef.current
    const container = scrollRef.current
    if (anchor === null || container === null) return

    prependAnchorRef.current = null
    // Читатель у конца переписки должен там и остаться: держим низ, а не
    // прежнее смещение. Иначе автодогрузка первых страниц (история урока)
    // оставляла бы его в середине разговора вместо последних сообщений.
    const stickToBottom = anchor.atBottom
    container.scrollTop = stickToBottom
      ? container.scrollHeight
      : container.scrollHeight - anchor.scrollHeight + anchor.scrollTop

    // Картинки в подклеенных сообщениях приходят позже разметки и вырастают
    // уже после компенсации, снова утягивая ленту. Досчитываем их высоту по
    // мере загрузки — но только для того, что оказалось выше экрана: картинка
    // в видимой части ничего под собой не двигает.
    let settledHeight = container.scrollHeight
    const pending = Array.from(container.querySelectorAll('img')).filter(
      (image) => !image.complete
    )

    for (const image of pending) {
      const onSettle = () => {
        const grown = container.scrollHeight - settledHeight
        settledHeight = container.scrollHeight
        if (grown <= 0) return

        if (stickToBottom) {
          container.scrollTop = container.scrollHeight

          return
        }

        if (image.getBoundingClientRect().bottom <= container.getBoundingClientRect().top) {
          container.scrollTop += grown
        }
      }

      image.addEventListener('load', onSettle, { once: true })
      image.addEventListener('error', onSettle, { once: true })
    }
  }, [messages])

  useEffect(() => {
    if (roomId === null || messages.length === 0) return

    const lastId = messages[messages.length - 1]?.id ?? null
    if (lastId === lastSeenIdRef.current) return
    const isFirstPosition = lastSeenIdRef.current === null
    lastSeenIdRef.current = lastId

    // Скроллим свой контейнер, а не элемент: scrollIntoView тянет за собой
    // всех прокручиваемых предков, и на странице урока новое сообщение
    // уводило бы вниз весь документ.
    const container = scrollRef.current
    if (container !== null) {
      // Первый показ комнаты — мгновенно: плавная прокрутка идёт кадрами и не
      // успевает доехать до низа, а подклейка истории застаёт ленту на полпути
      // и закрепляет её там.
      container.scrollTo({
        top: container.scrollHeight,
        behavior: isFirstPosition ? 'auto' : 'smooth',
      })
    }

    if (!autoMarkRead) return
    if (lastId !== null) void markRead(roomId, lastId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, roomId])

  // Догрузка всей истории по странице за раз. Объявлена после автоскролла:
  // эффекты выполняются в порядке объявления, и замер положения ленты обязан
  // случиться уже после того, как её поставили в конец переписки.
  // Потолок — на случай аномально длинной комнаты.
  useEffect(() => {
    if (!autoLoadAll || roomId === null) return
    if (roomState?.loaded !== true || roomState.isLoading) return
    if (!roomState.hasMore) return

    if (autoLoadedPagesRef.current >= AUTO_LOAD_PAGE_LIMIT) {
      // Комната неожиданно длинная: дальше не тянем сами, но и не прячем
      // остаток истории — возвращаем кнопку.
      setAutoLoadExhausted(true)

      return
    }

    autoLoadedPagesRef.current += 1
    loadOlder()
  }, [autoLoadAll, roomId, roomState?.loaded, roomState?.isLoading, roomState?.hasMore, loadOlder])

  // Группы считаются в рендере, а не при загрузке: так они сами
  // пересчитываются и после «Загрузить ещё» (старая страница подклеивается
  // в начало), и после realtime-сообщения в конце.
  //
  // Результат — именно группы, а не помеченные сообщения: липкая плашка
  // ограничена своим блоком, и обёртка вокруг одного пузыря держала бы её
  // ровно на его высоту.
  const dayGroups = useMemo(() => {
    const groups: Array<{ key: string; dividerIso: string | null; messages: ChatMessage[] }> = []
    let currentDay: string | null = null

    for (const message of messages) {
      const day = localDayKey(message.created_at)

      // created_at может быть null — такое сообщение остаётся в текущей
      // группе и день не переключает.
      if (day !== null && day !== currentDay) {
        currentDay = day
        groups.push({ key: `day-${day}-${message.id}`, dividerIso: message.created_at, messages: [message] })

        continue
      }

      if (groups.length === 0) {
        groups.push({ key: `day-none-${message.id}`, dividerIso: null, messages: [message] })

        continue
      }

      groups[groups.length - 1].messages.push(message)
    }

    return groups
  }, [messages])

  const showSkeleton = (roomState?.isLoading ?? false) && messages.length === 0
  const loadError = roomState?.error ?? null

  return (
    <Box
      ref={scrollRef}
      sx={{
        flex: 1,
        overflow: 'auto',
        p: dense ? 1 : 2,
        // Своя компенсация прокрутки (см. useLayoutEffect выше) складывалась бы
        // с браузерной: Chrome при подклейке истории двигает ленту сам, и вдвоём
        // они уносили читателя дальше, чем нужно.
        overflowAnchor: 'none',
      }}
    >
      {loadError !== null && (
        // Показывается и при непустой ленте: упавшая догрузка иначе выглядела
        // бы как кнопка, которая ничего не делает.
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={roomState?.isLoading ?? false}
              onClick={() => roomId !== null && fetchMessages(roomId)}
            >
              {t('common.retry')}
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      {roomState?.hasMore && (!autoLoadAll || autoLoadExhausted) && (
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <IconButton
            size="small"
            disabled={roomState?.isLoading ?? false}
            onClick={loadOlder}
          >
            <Typography variant="caption">{t('chat.loadMore')}</Typography>
          </IconButton>
        </Box>
      )}
      {showSkeleton ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={48}
              width={i % 2 === 0 ? '70%' : '55%'}
              sx={{ alignSelf: i % 2 === 0 ? 'flex-start' : 'flex-end' }}
            />
          ))}
        </Box>
      ) : messages.length === 0 && emptyText && loadError === null ? (
        <Typography color="text.secondary" variant="body2">
          {emptyText}
        </Typography>
      ) : (
        dayGroups.map((group) => (
          // Блок на весь день: он и есть containing block липкой
          // плашки. Обёртка вокруг одного сообщения держала бы её
          // на высоту одного пузыря, а общий контейнер на всю ленту —
          // слепил бы плашки всех дней в стопку наверху.
          <Box key={group.key}>
            {group.dividerIso !== null && !hideDateDividers && (
              <ChatDateDivider iso={group.dividerIso} />
            )}
            {group.messages.map((message) =>
              // Плашка истории урока — служебная запись ленты, а не чей-то
              // пузырь: у неё нет ни текста, ни автора для показа. Удалённая
              // (админом-модератором) рисуется обычным «сообщение удалено»:
              // открывать по ней уже нечего.
              !message.is_deleted && isLessonChatWidget(message.meta) ? (
                <LessonChatWidgetCard
                  key={message.id}
                  meta={message.meta}
                  onOpen={onOpenLessonHistory}
                />
              ) : (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwn={message.author_user_id === currentUserId}
                  showAuthor={showAuthor}
                  canWrite={canWrite}
                  currentUserId={currentUserId}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onCopyText={onCopyText}
                  onToggleReaction={onToggleReaction}
                  disableReactions={readOnly}
                />
              )
            )}
          </Box>
        ))
      )}
    </Box>
  )
}
